import { useMutation, useQuery, UseQueryOptions } from "react-query";
import {
    IModule,
    IModuleFavoriteResponse,
    IModuleGetProgressPayload,
    IModuleProgress,
    IModuleSetProgressPayload,
    IModulesGetProgressPayload,
    IModuleUnitProgress
} from "../interfaces/modules.interface";
import {
    checkModuleFavorite,
    getFavoriteModules,
    getFavoriteModulesIds,
    getModule,
    getModuleProgress,
    getModules,
    getModulesProgress,
    getPathwayModules,
    getPsycoEducationalModule,
    getUnit,
    setModuleFavorite,
    setModuleProgress,
    setModuleUnfavorite
} from "../services/modules.service";
import { IUser } from "../interfaces/user.interface";

export function useGetModules<T extends any = IModule[]>(
    locale: string,
    user?: IUser,
    pathway?: string,
    options?: UseQueryOptions<any, unknown, T>
) {
    return useQuery({
        queryKey: ["modules", locale, pathway, user],
        queryFn: () => {if(pathway) return getModules({ locale, user }).then((r) => r.data)},
        retry: 1,
        refetchOnWindowFocus: false,
        staleTime: 15 * 60 * 1000,
        ...options
    });
}

export function useGetPathwayModules<T extends any = string[]>(
    pathway: string,
    options?: UseQueryOptions<any, unknown, T>
) {
    return useQuery({
        queryKey: ["pathways", pathway],
        queryFn: () => {if(pathway) return getPathwayModules({ pathway }).then((r) => r.data)},
        retry: 1,
        refetchOnWindowFocus: false,
        staleTime: 15 * 60 * 1000,
        ...options
    });
}

export function useGetModuleFavorite() {
    return useMutation((id: string) =>
        checkModuleFavorite(id).then((d) =>
            d.success ? (d.data as IModuleFavoriteResponse).favourite : false
        )
    );
}

export function useGetModulesProgress() {
    return useMutation((payload: IModulesGetProgressPayload) =>
        getModulesProgress(payload).then((d) =>
            d.success ? (d.data as IModuleProgress[]) : undefined
        )
    );
}

export function useGetModuleProgress() {
    return useMutation((payload: IModuleGetProgressPayload) =>
        getModuleProgress(payload).then((d) =>
            d.success ? (d.data as IModuleUnitProgress) : undefined
        )
    );
}

export function useGetModule<T extends any = IModule>(
    documentId?: string,
    locale?: string,
    user?: IUser,
    filters?: any,
    options?: UseQueryOptions<any, unknown, T>
) {
    return useQuery({
        queryKey: ["module", documentId, locale, filters],
        queryFn: () => {
            if (!locale) return;

            return getModule({ documentId, locale, filters, user }).then((r) => r.data);
        },
        retry: 1,
        refetchOnWindowFocus: false,
        staleTime: 15 * 60 * 1000,
        ...options
    });
}

export function useGetPEModule<T extends any = IModule>() {
    return useMutation((payload: { locale?: string; }) => getPsycoEducationalModule(payload));
}

export function useGetUnit<T extends any = IModule[]>(
    id?: string,
    user?: IUser,
    options?: UseQueryOptions<any, unknown, T>
) {
    return useQuery({
        queryKey: ["unit", id, user],
        queryFn: () => {
            if (!id) return;
            return getUnit(id, user).then((r) => r.data);
        },
        retry: 1,
        refetchOnWindowFocus: false,
        staleTime: 15 * 60 * 1000,
        ...options
    });
}

export function useSetModuleProgress() {
    return useMutation((payload: IModuleSetProgressPayload) => setModuleProgress(payload));
}

export function useSetModuleFavorite() {
    return useMutation((payload: string) => setModuleFavorite(payload));
}

export function useSetModuleUnfavorite() {
    return useMutation((payload: string) => setModuleUnfavorite(payload));
}

export function useGetFavoriteModulesIds() {
    return useMutation(() => getFavoriteModulesIds());
}

export function useGetFavorites() {
    return useMutation((payload: { favoritesIds: string[]; locale: string }) =>
        getFavoriteModules(payload)
    );
}
